import { useTranslations, useLocale } from "next-intl";
import moment from "moment-timezone";
import { useSession } from "next-auth/react";
import Image from "next/image";

const CurrentEventBanner = ({ currentEvent }) => {
    const t = useTranslations();
    const locale = useLocale();
    const { data: session } = useSession();
    const timezone = session?.user?.timezone;

    if (!currentEvent) return null;

    const formatEndDate = (endDate) => {
        return moment(endDate).locale(locale).format("LLL");
    };

    const getEventIcon = (eventType) => {
        switch (eventType) {
            case "double_chest":
                return (
                    <Image
                        src="/static/icons/chests/legendary.svg"
                        width={14}
                        height={14}
                        alt="Chest"
                    />
                );
            case "xp_boost":
                return (
                    <Image
                        src="/static/icons/xp.svg"
                        width={14}
                        height={14}
                        alt="XP"
                    />
                );
            case "fragment_boost":
                return (
                    <Image
                        src="/static/icons/fragment.svg"
                        width={14}
                        height={14}
                        alt="Fragment"
                    />
                );
            case "shield_boost":
                return (
                    <Image
                        src="/static/icons/shield.svg"
                        width={14}
                        height={14}
                        alt="Shield"
                    />
                );
            default:
                return (
                    <Image
                        src="/static/icons/pin.svg"
                        width={14}
                        height={14}
                        alt="Pin"
                    />
                );
        }
    };

    const getEventMessages = (event) => {
        const messages = [];

        if (event.doubleChest) {
            messages.push(`${t("event_double_chest")}`);
        }

        if (event.xpMultiplier > 1) {
            messages.push(
                `${t("event_xp_boost", { multiplier: event.xpMultiplier })}`,
            );
        }

        if (event.fragmentMultiplier > 1) {
            messages.push(
                `${t("event_fragment_boost", {
                    multiplier: event.fragmentMultiplier,
                })}`,
            );
        }

        if (event.shieldMultiplier > 1) {
            messages.push(
                `${t("event_shield_boost", {
                    multiplier: event.shieldMultiplier,
                })}`,
            );
        }

        return messages.length > 0
            ? messages
            : [currentEvent.description || currentEvent.name];
    };

    const eventMessages = getEventMessages(currentEvent);

    return (
        <div className="relative overflow-hidden bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-900 dark:to-purple-900 rounded-lg p-3 mb-4 shadow-md">
            {/* Logo Powered by Believemy style tampon en arrière-plan */}
            <div
                className="absolute top-1/2 left-1/3 transform -translate-y-1/2 opacity-10 hover:opacity-10 transition-all duration-500 rotate-12 hover:rotate-6 pointer-events-none"
                style={{
                    width: "150px",
                    height: "200px",
                    left: "90%",
                    top: "100%",
                    marginLeft: "-100px",
                    marginTop: "-50px",
                }}
            >
                <img
                    src="/images/site/brand/powered-by-believemy-white.svg"
                    alt="Powered by Believemy"
                    className="w-[200px] h-[200px] filter grayscale"
                    style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        objectFit: "contain",
                    }}
                />
            </div>

            <div className="relative z-10">
                <h3 className="font-semibold text-white mb-1">
                    {t("event_available", {
                        numberOfEvents: eventMessages.length,
                    })}
                </h3>

                <div className="space-y-0.5 mb-2">
                    {eventMessages.map((message, index) => (
                        <div key={index} className="text-sm text-white/90">
                            {message}
                        </div>
                    ))}
                </div>

                <div className="inline-flex items-center gap-1 text-xs text-white/75 mb-2">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    {t("event_end_date", {
                        date: formatEndDate(currentEvent.endDate),
                    })}
                </div>

                {currentEvent.premiumOnly && (
                    <div className="inline-flex items-center gap-1 bg-yellow-400/20 text-yellow-100 px-2 py-0.5 rounded-full text-xs">
                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse"></span>
                        {t("premium_event_only")}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CurrentEventBanner;
